import { useState } from 'react';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import AdminTabs from '../../components/admin/AdminTabs';
import Modal from '../../components/admin/Modal';
import { useAdminQueries, type QueryInput } from '../../hooks/useAdminQueries';
import type { QueryDto } from '@shared/types';

const EMPTY: QueryInput = {
  label: '',
  queryString: '',
  isEnabled: true,
};

export default function AdminQueries() {
  const { list, create, update, remove } = useAdminQueries();
  const [editing, setEditing] = useState<QueryDto | null>(null);
  const [adding, setAdding] = useState(false);

  const showForm = adding || editing !== null;

  const onSave = async (input: QueryInput) => {
    if (editing) await update.mutateAsync({ id: editing.id, ...input });
    else await create.mutateAsync(input);
    setAdding(false);
    setEditing(null);
  };

  const onDelete = async (q: QueryDto) => {
    if (!confirm(`Smazat dotaz "${q.label}"?`)) return;
    await remove.mutateAsync(q.id);
  };

  return (
    <div>
      <h1 className="mb-2 font-heading text-2xl font-semibold text-dark">Administrace</h1>
      <AdminTabs />

      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {list.data
            ? `${list.data.length} dotazů, ${list.data.filter((q) => q.isEnabled).length} aktivních`
            : 'Načítám…'}
        </div>
        <button onClick={() => setAdding(true)} className="btn-primary">
          <Plus className="h-4 w-4" /> Přidat dotaz
        </button>
      </div>

      <p className="mb-4 text-xs text-gray-500">
        Topic queries jsou Google News vyhledávání publikováno-agnostická.
        Ingest fetchuje{' '}
        <code className="rounded bg-gray-100 px-1 text-[11px]">
          news.google.com/rss/search?q=&lt;query&gt;
        </code>{' '}
        a zachytí stejné téma napříč všemi zdroji. Trust score se per-článek dohledává
        podle publishera (z tabulky zdrojů, default 50).
      </p>

      {list.data && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
              <tr>
                <th className="px-4 py-3">Label</th>
                <th className="px-4 py-3">Query</th>
                <th className="px-4 py-3">Stav</th>
                <th className="px-4 py-3 text-right">Akce</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {list.data.map((q) => (
                <tr key={q.id} className={q.isEnabled ? '' : 'opacity-50'}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-dark">{q.label}</div>
                  </td>
                  <td className="px-4 py-3">
                    <code className="text-xs text-gray-600">{q.queryString}</code>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() =>
                        update.mutate({
                          id: q.id,
                          label: q.label,
                          queryString: q.queryString,
                          isEnabled: !q.isEnabled,
                        })
                      }
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                        q.isEnabled ? 'bg-positive-bg text-positive' : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {q.isEnabled ? 'Aktivní' : 'Vypnutý'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => setEditing(q)} className="btn-ghost" title="Upravit">
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => onDelete(q)}
                        className="btn-ghost text-danger hover:text-danger"
                        title="Smazat"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <QueryForm
          initial={
            editing
              ? { label: editing.label, queryString: editing.queryString, isEnabled: editing.isEnabled }
              : EMPTY
          }
          isNew={editing === null}
          onSave={onSave}
          onCancel={() => {
            setAdding(false);
            setEditing(null);
          }}
          saving={create.isPending || update.isPending}
        />
      )}
    </div>
  );
}

function QueryForm({
  initial,
  isNew,
  onSave,
  onCancel,
  saving,
}: {
  initial: QueryInput;
  isNew: boolean;
  onSave: (input: QueryInput) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
}) {
  const [form, setForm] = useState(initial);

  const submit = async () => {
    if (!form.label.trim() || !form.queryString.trim()) return;
    await onSave(form);
  };

  return (
    <Modal
      title={isNew ? 'Nový topic query' : 'Upravit topic query'}
      onClose={onCancel}
      footer={
        <>
          <button onClick={onCancel} className="btn-ghost" disabled={saving}>
            Zrušit
          </button>
          <button onClick={submit} className="btn-primary" disabled={saving}>
            {saving ? 'Ukládám…' : 'Uložit'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="label">Label (pro admin orientaci)</label>
          <input
            className="input"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder="Vízová pravidla"
          />
        </div>

        <div>
          <label className="label">Google News query</label>
          <textarea
            className="input min-h-[80px] font-mono text-xs"
            value={form.queryString}
            onChange={(e) => setForm({ ...form, queryString: e.target.value })}
            placeholder='"UAE" ("golden visa" OR "investor visa")'
          />
          <p className="mt-1 text-xs text-gray-500">
            Podporuje Google operátory: uvozovky pro frázi, OR, závorky, site:.
          </p>
        </div>

        <div>
          <label className="mt-2 flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={form.isEnabled}
              onChange={(e) => setForm({ ...form, isEnabled: e.target.checked })}
            />
            <span className="text-sm">Aktivní</span>
          </label>
        </div>
      </div>
    </Modal>
  );
}
